/**
 * design_handoff_auth §5 — Google OAuth callback landing page: routes to
 * the "no account yet" notice, the simplified registration form, or straight
 * into the app if a token/session came back.
 * Created by piotr.pozniak@thebeaverhead.com on 23/11/2022.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import AuthLayout from "./AuthPages/AuthLayout";
import TestimonialPane from "./AuthPages/TestimonialPane";
import AuthAlert from "./AuthPages/AuthAlert";
import { useUserStore } from "../../../hooks/redux/user";
import AxiosConfig from "./../../../AxiosConfig";
import LoadingIndicator from "./../../calendar/components/LoadingIndicator";
import useAuthHook from "./../hooks/useAuthHook";
import AccountNotFound from "./AccountVerificationPage/AccountNotFound";
import SignUpForm from "./AccountVerificationPage/SignUpForm";
import { useOrganizationMembersStore } from "../../../hooks/redux/organizationMembers";
import useRedirectUrl from "../hooks/useRedirectUrl";
import { hasOnboardingCompleted } from "../helpers/organization";
import { useCurrentOrganizationStore } from "../../../hooks/redux/currentOrganization";
import styles from "./AuthPages/authForm.module.scss";

const AccountVerificationPage = (props) => {
  const history = useHistory();
  const [showLoading, setShowLoading] = useState(true);
  const [showNoAccount, setShowNoAccount] = useState(false);
  const [authError, setAuthError] = useState(null);

  // flow can be either "login" or "register"
  const [flow, setFlow] = useState("login");
  const { user, checkSession } = useUserStore();
  const [flowParams, setFlowParams] = useState();
  const { currentOrganization } = useCurrentOrganizationStore();

  const { organizationMembers, checkInvitation } = useOrganizationMembersStore();
  const { invitationOrganizationData } = organizationMembers;

  const [showForm, setShowForm] = useState(false);
  const [oAuthData, setOAuthData] = useState(null);
  const { redirectOrNavigateTo } = useRedirectUrl();

  useAuthHook();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const token = params.get("token");
    const error = params.get("error");
    const accessToken = params.get("access_token");
    let _flow = params.get("flow");

    if (!_flow) {
      setShowLoading(false);
      setAuthError("This verification link is invalid or has expired.");
      return () => {};
    }

    const _params = _flow ? _flow.split("&") : [];

    if (_params.length > 1) {
      _flow = _params.shift();
    }

    const flowParams = _params
      .map((item) => {
        const [key, value] = item.split("=");
        const param = {};
        param[key] = value;
        return param;
      })
      .reduce((acc, item) => {
        return { ...acc, ...item };
      }, {});

    setFlowParams(flowParams);
    setFlow(_flow);

    if (error && error !== "") {
      setAuthError(error);
    } else if (token && token !== "" && !user.checkSession) {
      AxiosConfig.setAuthToken(token);
      checkSession();
    } else if (accessToken && accessToken !== "") {
      setOAuthData({
        accessToken,
        refreshToken: params.get("refresh_token"),
        expiresIn: params.get("expires_in"),
        email: params.get("email"),
        firstName: params.get("first_name"),
        lastName: params.get("last_name"),
        organization_uuid: flowParams.organization_uuid,
      });

      if (flowParams && flowParams.invitationHash) {
        checkInvitation({
          invitationHash: flowParams.invitationHash,
          email: params.get("email"),
        });
      }

      if (_flow === "register") {
        setShowForm(true);
      } else {
        setShowNoAccount(true);
      }
    }

    if (_flow === "register" && flowParams && flowParams.invitationHash) {
      setShowForm(false);
      setShowLoading(true);
    } else {
      setShowLoading(false);
    }

    let redirectUrlParam = "";
    if (flowParams && flowParams.redirect) {
      redirectUrlParam = "?redirect=" + atob(flowParams.redirect);
    }
    history.replace("/account-verification" + redirectUrlParam);
  }, [window.location.search]);

  useEffect(() => {
    if (organizationMembers.checkInvitationSuccess) {
      setShowLoading(false);
      setShowForm(true);
      setOAuthData({
        ...oAuthData,
        organizationName: organizationMembers.invitationOrganizationData.name,
        organizationUuid: organizationMembers.invitationOrganizationData.uuid,
      });
    }
  }, [organizationMembers.checkInvitationSuccess]);

  useEffect(() => {
    if (user.model && ["users", "admins"].indexOf(user.model.role) >= 0) {
      if (hasOnboardingCompleted(currentOrganization)) {
        redirectOrNavigateTo("/dashboard");
      } else {
        redirectOrNavigateTo("/onboarding");
      }
    }
  }, [user.checkSessionSuccess, currentOrganization]);

  const onCreateAccount = useCallback(() => {
    setShowForm(true);
  }, []);

  let content = (
    <div className={styles.fields}>
      <LoadingIndicator />
    </div>
  );

  if (showForm) {
    content = (
      <SignUpForm
        oAuthData={oAuthData}
        submitBtnText={
          invitationOrganizationData && invitationOrganizationData.name
            ? `Join ${invitationOrganizationData.name}`
            : undefined
        }
      />
    );
  } else if (showNoAccount) {
    content = <AccountNotFound email={oAuthData.email} onCreateAccount={onCreateAccount} />;
  } else if (authError) {
    content = (
      <div>
        <h1 className={styles.title}>Something went wrong</h1>
        <AuthAlert variant="error">{authError}</AuthAlert>
        <p className={styles.footLink}>
          <Link to="/login">Go back to login</Link>
        </p>
      </div>
    );
  }

  return <AuthLayout rightPane={<TestimonialPane />}>{content}</AuthLayout>;
};

export default AccountVerificationPage;
